/**
 * SOVRANOVA — VitalityView (VITALITY module UI)
 * Movement & fitness by age/body. Wellness/education only — NOT physiotherapy.
 * Flow: quick pre-screen -> routine list (for your age band) -> guided player.
 * Visuals are lean v0 (clean cue cards); richer 3D avatars come later.
 * Gated in App.jsx behind VITE_FEATURE_VITALITY.
 */
import { useState, useEffect } from 'react'
import { api } from '../../services/api'

// 18+ product — adult bands only (minor band removed 4 Sep 2026, pre-beta audit F3).
// Keep in step with backend VitalityService.AGE_BANDS.
const AGE_BANDS = [
  { id: 'young_adult', label: 'Young adult (18–29)' },
  { id: 'adult', label: 'Adult (30s–40s)' },
  { id: 'midlife', label: 'Midlife (50s)' },
  { id: 'older_adult', label: 'Older adult (60+)' },
]
const PRESENTATIONS = [
  { id: 'neutral', label: 'Prefer not to say' },
  { id: 'female', label: 'Female' },
  { id: 'male', label: 'Male' },
]

export function VitalityView({ onClose, onHome }) {
  const [step, setStep] = useState('loading')   // loading|screen|flagged|list|player|done|error
  const [error, setError] = useState('')
  const [questions, setQuestions] = useState([])
  const [answers, setAnswers] = useState({})
  const [ageBand, setAgeBand] = useState('adult')
  const [presentation, setPresentation] = useState('neutral')
  const [disclaimer, setDisclaimer] = useState('')
  const [screenMsg, setScreenMsg] = useState('')
  const [routines, setRoutines] = useState([])
  const [routine, setRoutine] = useState(null)
  const [moveIdx, setMoveIdx] = useState(0)
  const [startedAt, setStartedAt] = useState(0)

  useEffect(() => {
    let alive = true
    api.get('/vitality/screening')
      .then(r => {
        if (!alive) return
        setQuestions(r.questions || [])
        setDisclaimer(r.disclaimer || '')
        // Only adopt a stored band the UI still offers (legacy minor-band rows fall back to the default).
        if (r.profile?.age_band && AGE_BANDS.some(b => b.id === r.profile.age_band)) setAgeBand(r.profile.age_band)
        if (r.profile?.presentation) setPresentation(r.profile.presentation)
        if (r.cleared) loadCatalogue()
        else setStep('screen')
      })
      .catch(() => { if (alive) { setError('Could not load Vitality right now.'); setStep('error') } })
    return () => { alive = false }
  }, [])

  async function loadCatalogue() {
    try {
      const r = await api.get('/vitality/catalogue')
      if (r.cleared) { setRoutines(r.routines || []); if (r.disclaimer) setDisclaimer(r.disclaimer); setStep('list') }
      else { setScreenMsg(r.message || 'Please complete the quick pre-screen first.'); setStep('screen') }
    } catch { setError('Could not load routines.'); setStep('error') }
  }

  // Every question needs an explicit Yes/No before the screen can be submitted
  // (client-side guard; the server enforces the same rule and fails closed).
  const answeredCount = questions.filter(q => typeof answers[q.id] === 'boolean').length
  const allAnswered = questions.length > 0 && answeredCount === questions.length

  async function submitScreening() {
    if (!allAnswered) {
      setScreenMsg('Please answer every question with a Yes or a No.')
      return
    }
    try {
      const r = await api.post('/vitality/screening', { answers, ageBand, presentation })
      setScreenMsg(r.message || '')
      if (r.cleared) loadCatalogue()
      else setStep('flagged')
    } catch (e) {
      // api.js surfaces a 4xx as Error(body.error). Incomplete / invalid band:
      // stay on the screen and ask again — never treat it as a failure state.
      const code = e?.message || ''
      if (code === 'screening_incomplete') { setScreenMsg('Please answer every question with a Yes or a No.'); return }
      if (code === 'age_band_invalid') { setScreenMsg('Please choose your age range.'); return }
      setError('Could not save your answers.'); setStep('error')
    }
  }

  async function openRoutine(id) {
    try {
      const r = await api.get(`/vitality/routine/${id}`)
      setRoutine(r.routine); setMoveIdx(0); setStartedAt(Date.now()); setStep('player')
    } catch { setError('Could not open that routine.'); setStep('error') }
  }

  async function finishRoutine() {
    try {
      await api.post('/vitality/complete', { routineId: routine.id, durationSec: Math.round((Date.now() - startedAt) / 1000) })
    } catch (_) {}
    setStep('done')
  }

  const Header = (
    <div style={m.head}>
      <button style={m.back} onClick={onHome || onClose} aria-label="Back">←</button>
      <span style={m.title}>🏃 VITALITY</span>
      <span style={m.sub}>Movement & fitness</span>
    </div>
  )

  if (step === 'loading') return <div style={m.wrap}>{Header}<p style={m.dim}>Loading…</p></div>
  if (step === 'error') return <div style={m.wrap}>{Header}<p style={m.dim}>{error}</p><button style={m.btn} onClick={onHome || onClose}>Back</button></div>

  if (step === 'screen') return (
    <div style={m.wrap}>
      {Header}
      <p style={m.lead}>A quick, private pre-screen so we can suggest movement that suits your body.</p>
      <label style={m.lbl}>Age range</label>
      <select style={m.sel} value={ageBand} onChange={e => setAgeBand(e.target.value)}>
        {AGE_BANDS.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
      </select>
      <label style={m.lbl}>Body (helps tailor routines)</label>
      <select style={m.sel} value={presentation} onChange={e => setPresentation(e.target.value)}>
        {PRESENTATIONS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
      </select>
      <p style={m.lbl}>Answer honestly — every question needs a Yes or a No. If any answer is Yes, we won’t suggest routines and will point you to your doctor or an exercise professional first.</p>
      {questions.map(q => (
        <div key={q.id} style={m.qrow}>
          <span style={m.qtext}>{q.q}</span>
          <div style={m.toggle}>
            <button style={{ ...m.tog, ...(answers[q.id] === false ? m.togOn : {}) }} onClick={() => setAnswers(a => ({ ...a, [q.id]: false }))}>No</button>
            <button style={{ ...m.tog, ...(answers[q.id] === true ? m.togYes : {}) }} onClick={() => setAnswers(a => ({ ...a, [q.id]: true }))}>Yes</button>
          </div>
        </div>
      ))}
      <p style={m.lbl}>{answeredCount} of {questions.length} answered</p>
      {screenMsg && <p style={m.note}>{screenMsg}</p>}
      <button style={{ ...m.btn, ...(allAnswered ? {} : m.btnOff) }} onClick={submitScreening} disabled={!allAnswered} aria-disabled={!allAnswered}>Continue</button>
      <p style={m.disc}>{disclaimer}</p>
    </div>
  )

  // A "yes" answer: no routines are offered. Plain wording, no medical
  // decision, no hidden bypass — reviewing answers is an explicit step and the
  // copy says a changed answer is self-report, not clearance.
  if (step === 'flagged') return (
    <div style={m.wrap}>
      {Header}
      <p style={m.note}>{screenMsg}</p>
      <button style={m.btnGhost} onClick={() => { setScreenMsg(''); setStep('screen') }}>Review my answers</button>
      <button style={m.btnGhost} onClick={onHome || onClose}>Home</button>
      <p style={m.disc}>{disclaimer}</p>
    </div>
  )

  if (step === 'list') return (
    <div style={m.wrap}>
      {Header}
      <p style={m.lead}>Short, gentle routines for you. Move within a comfortable range.</p>
      {routines.map(r => (
        <button key={r.id} style={m.card} onClick={() => openRoutine(r.id)}>
          <span style={m.cardTitle}>{r.title}</span>
          <span style={m.cardFocus}>{r.focus}</span>
          <span style={m.cardMeta}>{r.durationMin} min · {r.level} · {r.moves} moves</span>
        </button>
      ))}
      <p style={m.disc}>{disclaimer}</p>
    </div>
  )

  if (step === 'player' && routine) {
    const mv = routine.movements[moveIdx]
    const last = moveIdx >= routine.movements.length - 1
    return (
      <div style={m.wrap}>
        {Header}
        <p style={m.routineTitle}>{routine.title}</p>
        <div style={m.stage}>
          <div style={m.avatar}>🧍</div>
          <p style={m.moveName}>{mv.name}</p>
        </div>
        <p style={m.cue}>{mv.cue}</p>
        <p style={m.dose}>{mv.dose}</p>
        <p style={m.scale}>Easier: {mv.regression} · Harder: {mv.progression}</p>
        <div style={m.playerNav}>
          <button style={m.navBtn} disabled={moveIdx === 0} onClick={() => setMoveIdx(i => Math.max(0, i - 1))}>Back</button>
          <span style={m.count}>{moveIdx + 1} / {routine.movements.length}</span>
          {last
            ? <button style={m.btnGo} onClick={finishRoutine}>Finish</button>
            : <button style={m.btnGo} onClick={() => setMoveIdx(i => i + 1)}>Next</button>}
        </div>
        <p style={m.disc}>{routine.disclaimer}</p>
      </div>
    )
  }

  if (step === 'done') return (
    <div style={m.wrap}>
      {Header}
      <div style={m.doneWrap}>
        <div style={m.doneIcon}>✓</div>
        <p style={m.doneMsg}>Nicely done. Small, regular movement adds up.</p>
        <button style={m.btn} onClick={() => setStep('list')}>Another routine</button>
        <button style={m.btnGhost} onClick={onHome || onClose}>Home</button>
      </div>
    </div>
  )

  return null
}

const m = {
  wrap:    { minHeight:'100vh', background:'linear-gradient(165deg,#0e1119,#161922)', color:'#e8eef4', padding:'1rem 1.1rem 3rem', maxWidth:560, margin:'0 auto' },
  head:    { display:'flex', alignItems:'center', gap:'0.6rem', padding:'0.4rem 0 1rem' },
  back:    { background:'transparent', border:'none', color:'#e8eef4', fontSize:'1.4rem', cursor:'pointer', lineHeight:1 },
  title:   { fontWeight:800, letterSpacing:'0.04em', fontSize:'1rem' },
  sub:     { color:'rgba(240,244,248,.45)', fontSize:'0.75rem', marginLeft:'auto' },
  lead:    { color:'rgba(240,244,248,.7)', fontSize:'0.9rem', lineHeight:1.5, margin:'0 0 1rem' },
  lbl:     { display:'block', fontSize:'0.75rem', color:'rgba(240,244,248,.5)', margin:'0.8rem 0 0.3rem', letterSpacing:'0.03em' },
  sel:     { width:'100%', padding:'0.7rem', borderRadius:'0.7rem', background:'rgba(255,255,255,.06)', color:'#f0f4f8', border:'1px solid rgba(255,255,255,.1)', fontSize:'0.9rem' },
  qrow:    { display:'flex', alignItems:'center', gap:'0.6rem', padding:'0.6rem 0', borderBottom:'1px solid rgba(255,255,255,.06)' },
  qtext:   { flex:1, fontSize:'0.82rem', color:'#dbe4ee', lineHeight:1.4 },
  toggle:  { display:'flex', gap:'0.35rem', flexShrink:0 },
  tog:     { padding:'0.4rem 0.7rem', borderRadius:'0.6rem', background:'rgba(255,255,255,.06)', color:'#cdd7e2', border:'1px solid rgba(255,255,255,.1)', cursor:'pointer', fontSize:'0.8rem' },
  togOn:   { background:'rgba(139,92,246,.35)', color:'#fff', borderColor:'rgba(139,92,246,.6)' },
  togYes:  { background:'rgba(230,160,60,.3)', color:'#fff', borderColor:'rgba(230,160,60,.55)' },
  note:    { background:'rgba(230,160,60,.12)', border:'1px solid rgba(230,160,60,.3)', borderRadius:'0.7rem', padding:'0.7rem', fontSize:'0.82rem', color:'#f0d9b8', margin:'1rem 0 0', lineHeight:1.5 },
  btn:     { width:'100%', padding:'0.85rem', borderRadius:'0.8rem', background:'#8b5cf6', color:'#fff', border:'none', fontWeight:700, fontSize:'0.92rem', cursor:'pointer', marginTop:'1.2rem' },
  btnOff:  { opacity:0.45, cursor:'not-allowed' },
  btnGhost:{ width:'100%', padding:'0.75rem', borderRadius:'0.8rem', background:'transparent', color:'rgba(240,244,248,.7)', border:'1px solid rgba(255,255,255,.14)', fontSize:'0.88rem', cursor:'pointer', marginTop:'0.6rem' },
  disc:    { fontSize:'0.72rem', color:'rgba(240,244,248,.4)', lineHeight:1.5, marginTop:'1.4rem' },
  dim:     { color:'rgba(240,244,248,.55)', fontSize:'0.9rem' },
  card:    { display:'flex', flexDirection:'column', gap:'0.25rem', width:'100%', textAlign:'left', padding:'0.9rem', borderRadius:'0.9rem', background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.08)', cursor:'pointer', marginBottom:'0.7rem' },
  cardTitle:{ fontWeight:700, color:'#f0f4f8', fontSize:'0.95rem' },
  cardFocus:{ color:'rgba(240,244,248,.6)', fontSize:'0.8rem' },
  cardMeta:{ color:'rgba(167,139,250,.95)', fontSize:'0.72rem', marginTop:'0.2rem' },
  routineTitle:{ fontWeight:800, fontSize:'1.05rem', margin:'0 0 0.8rem' },
  stage:   { display:'flex', flexDirection:'column', alignItems:'center', gap:'0.6rem', background:'radial-gradient(circle at 50% 40%, rgba(139,92,246,.18), rgba(255,255,255,.03))', borderRadius:'1rem', padding:'1.6rem 1rem' },
  avatar:  { fontSize:'3.2rem' },
  moveName:{ fontWeight:700, fontSize:'1rem', color:'#fff' },
  cue:     { fontSize:'0.95rem', color:'#dbe4ee', lineHeight:1.5, margin:'1rem 0 0.4rem' },
  dose:    { fontWeight:700, color:'#a78bfa', fontSize:'0.9rem' },
  scale:   { fontSize:'0.76rem', color:'rgba(240,244,248,.5)', marginTop:'0.4rem' },
  playerNav:{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.6rem', marginTop:'1.2rem' },
  navBtn:  { padding:'0.7rem 1rem', borderRadius:'0.7rem', background:'rgba(255,255,255,.06)', color:'#e8eef4', border:'1px solid rgba(255,255,255,.12)', cursor:'pointer', fontSize:'0.85rem' },
  count:   { color:'rgba(240,244,248,.55)', fontSize:'0.8rem' },
  btnGo:   { padding:'0.7rem 1.4rem', borderRadius:'0.7rem', background:'#8b5cf6', color:'#fff', border:'none', fontWeight:700, cursor:'pointer', fontSize:'0.88rem' },
  doneWrap:{ display:'flex', flexDirection:'column', alignItems:'center', gap:'0.8rem', padding:'2rem 1rem' },
  doneIcon:{ width:'3.4rem', height:'3.4rem', borderRadius:'50%', background:'rgba(139,92,246,.25)', color:'#c4b5fd', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.6rem' },
  doneMsg: { color:'#dbe4ee', textAlign:'center', fontSize:'0.95rem' },
}
